import React, { useState, useCallback, useMemo } from 'react';
import { DialogV2 } from '@withjoy/joykit';
import { PayoutAccountInfo } from './CreditCardSetUp.context';
import { Introduction } from './steps/Introduction';
import { InitHostForm } from './steps/InitHostForm';
import { OnboardingStatus } from './steps/OnboardingStatus';
import { ReadyToUse } from './steps/ReadyToUse';
import { pxToRem } from '@withjoy/joykit/theme';
import { useCreditCardForCashEligibility } from './useCreditCardForCashEligibility';

type DialogStep = 'introduction' | 'initHostForm' | 'onboarding' | 'readyToUse';

type CreditCardSetupInitiatorDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  isOwner: boolean;
  payoutAccountInfo?: PayoutAccountInfo | null;
  initKYCOnboarding: () => void;
  initPayoutAccountLoading: boolean;
  initKYCLaterOnboarding: (firstName: string, lastName: string) => Promise<boolean>;
  initKYCLaterLoading: boolean;
  maxAttemptsReached: boolean;
  isValidUSAddress: boolean;
  shouldSkipAddressValidation: boolean;
  handleInvalidShippingAddress?: (firstName?: string, lastName?: string) => void;
}>;

export const CreditCardSetupInitiatorDialog: React.FC<CreditCardSetupInitiatorDialogProps> = ({
  isOpen,
  onClose,
  isOwner,
  payoutAccountInfo,
  initKYCOnboarding,
  initPayoutAccountLoading,
  initKYCLaterOnboarding,
  initKYCLaterLoading,
  maxAttemptsReached,
  isValidUSAddress,
  shouldSkipAddressValidation,
  handleInvalidShippingAddress,
}) => {
  const { canEnableCreditCard, registryKycLaterEnabled } = useCreditCardForCashEligibility();
  const [overrideStep, setOverrideStep] = useState<DialogStep | null>(null);

  const activeStep: DialogStep = useMemo(() => {
    if (overrideStep) return overrideStep;
    if (canEnableCreditCard) return 'readyToUse';
    if (payoutAccountInfo?.status === 'pending') return 'onboarding';
    return registryKycLaterEnabled && isOwner ? 'initHostForm' : 'introduction';
  }, [overrideStep, payoutAccountInfo?.status, canEnableCreditCard, registryKycLaterEnabled, isOwner]);

  const handleClose = useCallback(() => {
    setOverrideStep(null);
    onClose();
  }, [onClose]);

  const handleInitHostFormSubmit = useCallback(
    async (firstName: string, lastName: string) => {
      const success = await initKYCLaterOnboarding(firstName, lastName);
      if (success) {
        if (!isValidUSAddress && handleInvalidShippingAddress) {
          handleInvalidShippingAddress(firstName, lastName);
          handleClose();
        } else {
          setOverrideStep('readyToUse');
        }
      }
    },
    [initKYCLaterOnboarding, isValidUSAddress, handleInvalidShippingAddress, handleClose],
  );

  const handleIntroductionInvalidAddress = useCallback(() => {
    handleInvalidShippingAddress?.();
    handleClose();
  }, [handleInvalidShippingAddress, handleClose]);

  return (
    <DialogV2
      onClose={handleClose}
      isOpen={isOpen}
      size={activeStep === 'initHostForm' ? 'lg' : undefined}
      overrides={
        activeStep !== 'initHostForm'
          ? {
              Content: {
                props: {
                  width: pxToRem(768),
                },
              },
            }
          : undefined
      }
    >
      <DialogV2.CloseButton />
      {activeStep === 'introduction' && (
        <Introduction
          payoutAccountInfo={payoutAccountInfo}
          initKYCOnboarding={initKYCOnboarding}
          isOwner={isOwner}
          initPayoutAccountLoading={initPayoutAccountLoading}
          isValidUSAddress={isValidUSAddress}
          shouldSkipAddressValidation={shouldSkipAddressValidation}
          handleInvalidShippingAddress={handleIntroductionInvalidAddress}
        />
      )}
      {activeStep === 'initHostForm' && (
        <InitHostForm
          onSubmit={handleInitHostFormSubmit}
          loading={initKYCLaterLoading}
        />
      )}
      {activeStep === 'onboarding' && payoutAccountInfo && (
        <OnboardingStatus
          onboardingUrl={payoutAccountInfo.onboardingUrl}
          maxAttemptsReached={maxAttemptsReached}
          status={payoutAccountInfo.status}
        />
      )}
      {activeStep === 'readyToUse' && <ReadyToUse onClose={handleClose} />}
    </DialogV2>
  );
};
